// Funciones de comunicación con la API de raceway
// BASE_URL debe ser relativa para pasar por el proxy nginx y evitar CSP con http://

import { Estado, TimeSettings } from '@/types/proyectos/raceway';

// ⚠️ IMPORTANTE: URL relativa → nginx proxia /api/ → localhost:5001
// Nunca usar http://localhost:5001 directamente (viola CSP en producción)
const BASE_URL = process.env.NEXT_PUBLIC_RACEWAY_BASE_URL ?? '/api/v1/raceway';

// ─── Estado ───────────────────────────────────────────────────────────────────

export async function fetchEstado(): Promise<Estado> {
  const res = await fetch(`${BASE_URL}/list-tanks`, { credentials: 'include' });
  if (!res.ok) throw new Error('Error al obtener el estado');
  return res.json();
}

// ─── Válvulas ─────────────────────────────────────────────────────────────────

const VALVULA_IDS: Record<string, number> = {
  v1: 1, v2: 2, v3: 3, v4: 4, v5: 5, v6: 6,
};

/**
 * Abre o cierra una válvula por su ID lógico (v1..v6).
 * Llama a POST /api/v1/raceway/valvula/{id}/{true|false}
 * que está definido en tank.py y pasa por nginx.
 */
export async function toggleValvula(valvula: string, abrir: boolean): Promise<void> {
  const id = VALVULA_IDS[valvula];
  if (!id) throw new Error(`Válvula desconocida: ${valvula}`);

  const res = await fetch(
    `${BASE_URL}/valvula/${id}/${abrir}`,
    { method: 'POST', credentials: 'include' }
  );

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).detail ?? 'Error al accionar válvula');
  }
}

// ─── Fases ────────────────────────────────────────────────────────────────────

/**
 * Ejecuta una fase en un depósito concreto.
 * POST /api/v1/raceway/execute-phase?tank={tank}&phase={phase}
 */
export async function executePhase(tank: string, phase: string): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/execute-phase?tank=${encodeURIComponent(tank)}&phase=${encodeURIComponent(phase)}`,
    { method: 'POST', credentials: 'include' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).detail ?? 'Error al ejecutar fase');
  }
}

/**
 * Cancela todas las fases activas de un depósito.
 * POST /api/v1/raceway/cancel-any-phase?tank={tank}
 */
export async function cancelPhase(tank: string): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/cancel-any-phase?tank=${encodeURIComponent(tank)}`,
    { method: 'POST', credentials: 'include' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).detail ?? 'Error al cancelar fase');
  }
}

/**
 * Cambia la duración de una fase (en segundos).
 * PUT /api/v1/raceway/set_phase_duration?tank={tank}&phase={phase}
 */
export async function setDuration(
  tank: string,
  phase: string,
  duration: number
): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/set_phase_duration?tank=${encodeURIComponent(tank)}&phase=${encodeURIComponent(phase)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration }),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).detail ?? 'Error al cambiar duración');
  }
}

// ─── Time Settings (configuración global de tiempos) ─────────────────────────

export async function fetchTimeSettings(): Promise<TimeSettings> {
  const res = await fetch(`${BASE_URL}/time-settings`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Error al obtener los tiempos');
  return res.json();
}

export async function saveTimeSettings(settings: TimeSettings): Promise<void> {
  const res = await fetch(`${BASE_URL}/time-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).detail ?? 'Error al guardar los tiempos');
  }
}